import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { useAuth, homePathFor } from "@/lib/auth";
import LoginPage from "@/pages/LoginPage";
import Shell from "@/pages/Shell";
import Dashboard from "@/pages/Dashboard";
import Incidents from "@/pages/Incidents";
import IncidentDetail from "@/pages/IncidentDetail";
import SLAConfig from "@/pages/SLAConfig";
import Assets from "@/pages/Assets";
import Problems from "@/pages/Problems";
import Changes from "@/pages/Changes";
import Reports from "@/pages/Reports";
import ReportBuilder from "@/pages/ReportBuilder";
import ReportsHub from "@/pages/ReportsHub";
import AdminHub from "@/pages/AdminHub";
import ClientReport from "@/pages/ClientReport";
import KnowledgeBase from "@/pages/KnowledgeBase";
import RoutingRules from "@/pages/RoutingRules";
import EndUserPortal from "@/pages/EndUserPortal";
import EmailIngest from "@/pages/EmailIngest";
import ScimSettings from "@/pages/ScimSettings";
import CannedResponses from "@/pages/CannedResponses";
import Groups from "@/pages/Groups";
import Users from "@/pages/Users";
import AlertEvents from "@/pages/AlertEvents";
import TeamsIntegration from "@/pages/TeamsIntegration";
import Notifications from "@/pages/Notifications";
import SecuritySettings from "@/pages/SecuritySettings";
import SessionActivity from "@/pages/SessionActivity";
import FeedbackSurvey from "@/pages/FeedbackSurvey";
import ChangeApproval from "@/pages/ChangeApproval";
import TimesheetLayout from "@/components/TimesheetLayout";
import Timesheet from "@/pages/Timesheet";
import TimesheetDashboard from "@/pages/TimesheetDashboard";
import TimesheetApprovals from "@/pages/TimesheetApprovals";
import TimesheetReports from "@/pages/TimesheetReports";
import TimesheetAdmin from "@/pages/TimesheetAdmin";
import SocLayout from "@/components/SocLayout";
import SocDashboard from "@/pages/SocDashboard";
import SocCases from "@/pages/SocCases";
import SocCaseDetail from "@/pages/SocCaseDetail";
import SocReports from "@/pages/SocReports";
import SocIocs from "@/pages/SocIocs";
import SocIntelLibrary from "@/pages/SocIntelLibrary";
import SocTrends from "@/pages/SocTrends";
import SocHandover from "@/pages/SocHandover";
import SocTeamLoad from "@/pages/SocTeamLoad";
import SocSettings from "@/pages/SocSettings";
import Clients from "@/pages/Clients";
import TasksLayout, { isTaskStaff } from "@/components/TasksLayout";
import { MyTasks, Projects, ProjectView, AllTasks, TaskRequestForm, TasksDashboard, TaskSettings, withCtx } from "@/pages/Tasks";

const TMyTasks = withCtx(MyTasks), TProjects = withCtx(Projects), TProjectView = withCtx(ProjectView), TAllTasks = withCtx(AllTasks),
  TRequestForm = withCtx(TaskRequestForm), TDashboard = withCtx(TasksDashboard);

const TaskStaffGate = ({ children }) => {
  const { user } = useAuth();
  if (user && !isTaskStaff(user)) return <Navigate to="/tasks/request" replace />;
  return children;
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <div className="h-screen flex items-center justify-center text-zinc-600 dark:text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Videowall viewer is locked to the SOC dashboard, wherever it tries to go.
  if (user.role === "soc_viewer" && pathname !== "/soc") return <Navigate to="/soc" replace />;
  return children;
};

const RequireRole = ({ allow, redirect = "/", children }) => {
  const { user } = useAuth();
  if (user && !allow.includes(user.role)) return <Navigate to={redirect} replace />;
  return children;
};

// Staff-only shell; non-staff go to their proper home (portal or timesheet).
const StaffGate = ({ children }) => {
  const { user } = useAuth();
  if (user && !["admin", "manager", "agent", "timesheet_admin"].includes(user.role)) return <Navigate to={homePathFor(user)} replace />;
  return children;
};

const canTimesheet = (u) => u && (u.timesheet_access || u.timesheet_role || u.role === "admin" || u.role === "timesheet_admin" || u.role === "soc_lead");
const isTsManager = (u) => u && (u.role === "admin" || u.role === "timesheet_admin" || u.role === "soc_lead" || ["manager", "admin"].includes(u.timesheet_role));
const isTsAdmin = (u) => u && (u.role === "admin" || u.role === "timesheet_admin" || u.timesheet_role === "admin");

const TimesheetGate = ({ children }) => {
  const { user } = useAuth();
  if (user && !canTimesheet(user)) return <Navigate to={homePathFor(user)} replace />;
  return children;
};
const TsManagerGate = ({ children }) => {
  const { user } = useAuth();
  if (user && !isTsManager(user)) return <Navigate to="/timesheet" replace />;
  return children;
};
const TsAdminGate = ({ children }) => {
  const { user } = useAuth();
  if (user && !isTsAdmin(user)) return <Navigate to="/timesheet" replace />;
  return children;
};

const isSoc = (u) => u && ["soc_analyst", "soc_lead", "soc_auditor", "soc_viewer", "admin"].includes(u.role);
const isSocLead = (u) => u && ["soc_lead", "admin"].includes(u.role);
const SocGate = ({ children }) => {
  const { user } = useAuth();
  if (user && !isSoc(user)) return <Navigate to={homePathFor(user)} replace />;
  return children;
};
const SocLeadGate = ({ children }) => {
  const { user } = useAuth();
  if (user && !isSocLead(user)) return <Navigate to="/soc" replace />;
  return children;
};
const SocAdminGate = ({ children }) => {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/soc" replace />;
  return children;
};

// timesheet_admin only gets Reports / Report Builder / Users within the Shell; block ITSM content routes.
const BlockTsAdmin = ({ children }) => {
  const { user } = useAuth();
  if (user && user.role === "timesheet_admin") return <Navigate to="/reports" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/feedback/:id" element={<FeedbackSurvey />} />
          <Route path="/approve/:token" element={<ChangeApproval />} />
          <Route path="/portal" element={<Protected><EndUserPortal /></Protected>} />
          <Route path="/timesheet" element={<Protected><TimesheetGate><TimesheetLayout /></TimesheetGate></Protected>}>
            <Route index element={<Timesheet />} />
            <Route path="dashboard" element={<TimesheetDashboard />} />
            <Route path="approvals" element={<TsManagerGate><TimesheetApprovals /></TsManagerGate>} />
            <Route path="reports" element={<TsManagerGate><TimesheetReports /></TsManagerGate>} />
            <Route path="admin" element={<TsAdminGate><TimesheetAdmin /></TsAdminGate>} />
          </Route>
          <Route path="/soc" element={<Protected><SocGate><SocLayout /></SocGate></Protected>}>
            <Route index element={<SocDashboard />} />
            <Route path="cases" element={<SocCases />} />
            <Route path="cases/:id" element={<SocCaseDetail />} />
            <Route path="iocs" element={<SocIocs />} />
            <Route path="library" element={<SocIntelLibrary />} />
            <Route path="trends" element={<SocTrends />} />
            <Route path="handover" element={<SocHandover />} />
            <Route path="team-load" element={<SocLeadGate><SocTeamLoad /></SocLeadGate>} />
            <Route path="reports" element={<SocReports />} />
            <Route path="settings" element={<SocLeadGate><SocSettings /></SocLeadGate>} />
            <Route path="clients" element={<SocAdminGate><Clients /></SocAdminGate>} />
          </Route>
          <Route path="/tasks" element={<Protected><TasksLayout /></Protected>}>
            <Route index element={<TaskStaffGate><TMyTasks /></TaskStaffGate>} />
            <Route path="projects" element={<TaskStaffGate><TProjects /></TaskStaffGate>} />
            <Route path="projects/:id" element={<TaskStaffGate><TProjectView /></TaskStaffGate>} />
            <Route path="all" element={<TaskStaffGate><TAllTasks /></TaskStaffGate>} />
            <Route path="dashboard" element={<TaskStaffGate><TDashboard /></TaskStaffGate>} />
            <Route path="request" element={<TRequestForm />} />
            <Route path="settings" element={<RequireRole allow={["admin"]} redirect="/tasks"><TaskSettings /></RequireRole>} />
          </Route>
          <Route path="/" element={<Protected><StaffGate><Shell /></StaffGate></Protected>}>
            <Route index element={<RequireRole allow={["admin","manager","agent"]} redirect="/reports"><Dashboard /></RequireRole>} />
            <Route path="incidents" element={<BlockTsAdmin><Incidents /></BlockTsAdmin>} />
            <Route path="incidents/:id" element={<BlockTsAdmin><IncidentDetail /></BlockTsAdmin>} />
            <Route path="reports" element={<ReportsHub />} />
            <Route path="report-builder" element={<RequireRole allow={["admin","manager","timesheet_admin"]}><ReportBuilder /></RequireRole>} />
            <Route path="client-report" element={<RequireRole allow={["admin","manager"]}><ClientReport /></RequireRole>} />
            <Route path="sla" element={<RequireRole allow={["admin"]}><SLAConfig /></RequireRole>} />
            <Route path="assets" element={<BlockTsAdmin><Assets /></BlockTsAdmin>} />
            <Route path="problems" element={<BlockTsAdmin><Problems /></BlockTsAdmin>} />
            <Route path="changes" element={<BlockTsAdmin><Changes /></BlockTsAdmin>} />
            <Route path="kb" element={<BlockTsAdmin><KnowledgeBase /></BlockTsAdmin>} />
            <Route path="routing" element={<RequireRole allow={["admin","manager"]}><RoutingRules /></RequireRole>} />
            <Route path="clients" element={<RequireRole allow={["admin"]}><Clients /></RequireRole>} />
            <Route path="email-ingest" element={<RequireRole allow={["admin"]}><EmailIngest /></RequireRole>} />
            <Route path="events" element={<RequireRole allow={["admin"]}><AlertEvents /></RequireRole>} />
            <Route path="scim" element={<RequireRole allow={["admin"]}><ScimSettings /></RequireRole>} />
            <Route path="canned" element={<BlockTsAdmin><CannedResponses /></BlockTsAdmin>} />
            <Route path="groups" element={<RequireRole allow={["admin","manager"]}><Groups /></RequireRole>} />
            <Route path="users" element={<RequireRole allow={["admin","timesheet_admin"]} redirect="/reports"><Users /></RequireRole>} />
            <Route path="teams" element={<RequireRole allow={["admin"]}><TeamsIntegration /></RequireRole>} />
            <Route path="notifications" element={<RequireRole allow={["admin"]}><Notifications /></RequireRole>} />
            <Route path="security" element={<RequireRole allow={["admin"]}><SecuritySettings /></RequireRole>} />
            <Route path="sessions" element={<RequireRole allow={["admin","manager"]}><SessionActivity /></RequireRole>} />
            <Route path="admin" element={<RequireRole allow={["admin","manager","agent"]} redirect="/reports"><AdminHub /></RequireRole>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
